import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hooks/useAuth';
import { onboardingApi, authApi } from '@services/api';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { ErrorDisplay } from '@components/common/ErrorDisplay';

const FOCUS_OPTIONS = [
  'Building Trust',
  'Resolving Conflict',
  'Inspiring Action',
  'Delivering Feedback',
  'Leading Change',
  'Coaching & Development',
  'Stakeholder Management',
  'Crisis Communication',
];

const DEFAULT_STAKEHOLDER_TAGS = [
  'Team Members',
  'Direct Reports',
  'Peers',
  'Senior Leadership',
  'Clients',
  'Vendors',
  'Board Members',
  'Customers',
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  
  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [primaryFocus, setPrimaryFocus] = useState('');
  const [stakeholderTags, setStakeholderTags] = useState<string[]>([]);
  const [customTag, setCustomTag] = useState('');
  
  // Fetch user profile to pre-fill email
  const { data: profileData, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const response = await authApi.getProfile();
      return response.data;
    },
  });
  
  const userEmail = profileData?.data?.email || '';
  const existingName = profileData?.data?.name || '';
  
  // Pre-fill name if it exists
  if (existingName && !name) {
    setName(existingName);
  }
  
  // Complete onboarding mutation
  const completeMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      phone: string;
      address: string;
      primary_focus: string;
      stakeholder_tags: string[];
    }) => {
      const response = await onboardingApi.complete(data);
      return response.data;
    },
    onSuccess: async () => {
      // Refresh auth state to get updated user roles (onboarding role removed)
      await checkAuth();
      
      // Navigate to dashboard (ProtectedRoute will no longer redirect to onboarding)
      navigate('/dashboard');
    },
  });
  
  const handleAddTag = (tag: string) => {
    if (!stakeholderTags.includes(tag)) {
      setStakeholderTags([...stakeholderTags, tag]);
    }
  };
  
  const handleRemoveTag = (tag: string) => {
    setStakeholderTags(stakeholderTags.filter((t) => t !== tag));
  };
  
  const handleAddCustomTag = () => {
    const trimmedTag = customTag.trim();
    if (trimmedTag && !stakeholderTags.includes(trimmedTag)) {
      setStakeholderTags([...stakeholderTags, trimmedTag]);
      setCustomTag('');
    }
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name || !primaryFocus || stakeholderTags.length === 0) {
      return;
    }
    
    completeMutation.mutate({
      name,
      phone,
      address,
      primary_focus: primaryFocus,
      stakeholder_tags: stakeholderTags,
    });
  };
  
  if (isLoadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome to SolasLite
          </h1>
          <p className="text-lg text-gray-600">
            Let's get you set up. This will only take a minute.
          </p>
        </div>
        
        {completeMutation.isError && (
          <div className="mb-6">
            <ErrorDisplay type="validation" message={completeMutation.error?.message || "Failed to complete onboarding"} />
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-8">
          <div className="space-y-8">
            {/* Personal Info Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Personal Information
              </h2>
              
              <div className="space-y-4">
                {/* Email (read-only) */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={userEmail}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="John Doe"
                    disabled={completeMutation.isPending}
                  />
                </div>
                
                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number (optional)
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="+1 (555) 123-4567"
                    disabled={completeMutation.isPending}
                  />
                </div>
                
                {/* Address */}
                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                    Address (optional)
                  </label>
                  <input
                    type="text"
                    id="address"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="123 Main St, City, State"
                    disabled={completeMutation.isPending}
                  />
                </div>
              </div>
            </div>
            
            {/* Communication Goals Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Step 1: Set Your Goal
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                What's your primary focus for improving your communication?
              </p>
              
              <select
                value={primaryFocus}
                onChange={(e) => setPrimaryFocus(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={completeMutation.isPending}
              >
                <option value="">Choose your focus area...</option>
                {FOCUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Stakeholder Tags Section */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Step 2: Add Context Tags
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Who do you communicate with? Select all that apply.
              </p>
              
              {/* Quick Select Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {DEFAULT_STAKEHOLDER_TAGS.map((tag) => {
                  const isSelected = stakeholderTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => isSelected ? handleRemoveTag(tag) : handleAddTag(tag)}
                      disabled={completeMutation.isPending}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        isSelected
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
              
              {/* Custom Tag Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customTag}
                  onChange={(e) => setCustomTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddCustomTag();
                    }
                  }}
                  placeholder="Add a custom tag..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={completeMutation.isPending}
                />
                <button
                  type="button"
                  onClick={handleAddCustomTag}
                  disabled={!customTag.trim() || completeMutation.isPending}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
              
              {/* Selected Tags Display */}
              {stakeholderTags.length > 0 && (
                <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-900 mb-2">
                    Selected ({stakeholderTags.length}):
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {stakeholderTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-600 text-white"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          disabled={completeMutation.isPending}
                          className="ml-2 hover:text-purple-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {stakeholderTags.length === 0 && (
                <p className="mt-2 text-sm text-red-600">
                  Please select at least one stakeholder tag
                </p>
              )}
            </div>
            
            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={
                  !name ||
                  !primaryFocus ||
                  stakeholderTags.length === 0 ||
                  completeMutation.isPending
                }
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-lg"
              >
                {completeMutation.isPending ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner size="sm" className="mr-2" />
                    Completing Setup...
                  </span>
                ) : (
                  'Complete Setup & Get Started'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

